<template>
  <div class="carousel-container">
    <div
      class="carousel group"
      @mouseenter="pause"
      @mouseleave="resume"
      @touchstart.passive="onTouchStart"
      @touchend="onTouchEnd"
    >
      <div class="carousel-inner" ref="carouselInner">
        <!-- The first image is repeated at the end so the loop never rewinds -->
        <div v-for="(image, index) in slides" :key="index" class="carousel-item">
          <img :src="image" alt="Carousel image" class="carousel-image" />
        </div>
      </div>

      <!-- Soft bottom fade so the dots stay readable -->
      <div class="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/45 to-transparent"></div>

      <button class="carousel-control prev" @click="prevSlide" aria-label="Previous slide">
        <i class="fa-solid fa-chevron-left"></i>
      </button>
      <button class="carousel-control next" @click="manualNext" aria-label="Next slide">
        <i class="fa-solid fa-chevron-right"></i>
      </button>
      <div class="carousel-dots">
        <button
          v-for="(image, index) in images"
          :key="index"
          class="dot"
          :class="{ active: index === activeDot }"
          :aria-label="`Go to slide ${index + 1}`"
          @click="goToSlide(index)"
        ></button>
      </div>
    </div>
  </div>
</template>

<script>
import gsap from "gsap";

import img1 from "@/assets/crousal/crousal1.jpeg";
import img2 from "@/assets/crousal/crousal2.jpeg";
import img3 from "@/assets/crousal/crousal3.jpeg";
import img4 from "@/assets/crousal/crousal4.jpeg";
import img6 from "@/assets/crousal/crousal6.jpeg";
import img7 from "@/assets/crousal/crousal7.jpeg";
import img8 from "@/assets/crousal/crousal8.jpeg";

const SLIDE_DURATION = 1.1; // seconds per transition
const AUTOPLAY_MS = 5500;

export default {
  name: "ImageCarousel",
  data() {
    return {
      images: [img1, img2, img3, img4, img6, img7, img8],
      currentIndex: 0,
      animating: false,
      intervalId: null,
      paused: false,
      touchStartX: 0,
    };
  },
  computed: {
    slides() {
      return [...this.images, this.images[0]];
    },
    activeDot() {
      return this.currentIndex % this.images.length;
    },
    duration() {
      const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      return reduce ? 0 : SLIDE_DURATION;
    },
  },
  methods: {
    slideImage(index) {
      const items = this.$refs.carouselInner?.children;
      return items && items[index] ? items[index].querySelector("img") : null;
    },
    // Slow zoom-out on the slide that is arriving
    kenBurns(index) {
      const img = this.slideImage(index);
      if (!img || this.duration === 0) return;
      gsap.fromTo(img, { scale: 1.08 }, { scale: 1, duration: 7, ease: "power1.out", overwrite: true });
    },
    animateTo(index, onDone) {
      this.animating = true;
      this.kenBurns(index);
      gsap.to(this.$refs.carouselInner, {
        xPercent: -index * 100,
        duration: this.duration,
        ease: "power3.inOut",
        onComplete: () => {
          this.animating = false;
          if (onDone) onDone();
        },
      });
    },
    nextSlide() {
      if (this.animating) return;
      const last = this.images.length;
      this.currentIndex += 1;
      this.animateTo(this.currentIndex, () => {
        // Landed on the cloned first slide: jump back to the real one invisibly
        if (this.currentIndex === last) {
          this.currentIndex = 0;
          gsap.set(this.$refs.carouselInner, { xPercent: 0 });
        }
      });
    },
    prevSlide() {
      if (this.animating) return;
      if (this.currentIndex === 0) {
        // Jump to the clone at the end first, then slide back one
        this.currentIndex = this.images.length;
        gsap.set(this.$refs.carouselInner, { xPercent: -this.currentIndex * 100 });
      }
      this.currentIndex -= 1;
      this.animateTo(this.currentIndex);
      this.restartAutoplay();
    },
    goToSlide(index) {
      if (this.animating || index === this.activeDot) return;
      this.currentIndex = index;
      this.animateTo(index);
      this.restartAutoplay();
    },
    manualNext() {
      this.nextSlide();
      this.restartAutoplay();
    },
    startAutoplay() {
      clearInterval(this.intervalId);
      this.intervalId = setInterval(() => {
        if (!this.paused && !document.hidden) this.nextSlide();
      }, AUTOPLAY_MS);
    },
    restartAutoplay() {
      this.startAutoplay();
    },
    pause() {
      this.paused = true;
    },
    resume() {
      this.paused = false;
    },
    onTouchStart(e) {
      this.touchStartX = e.changedTouches[0].clientX;
    },
    onTouchEnd(e) {
      const dx = e.changedTouches[0].clientX - this.touchStartX;
      if (Math.abs(dx) < 50) return;
      if (dx < 0) this.manualNext();
      else this.prevSlide();
    },
  },
  mounted() {
    gsap.set(this.$refs.carouselInner, { xPercent: 0 });
    this.$nextTick(() => this.kenBurns(0));
    this.startAutoplay();
  },
  beforeUnmount() {
    clearInterval(this.intervalId);
    gsap.killTweensOf(this.$refs.carouselInner);
  },
};
</script>


<style scoped>
.carousel-container {
  width: 100%;
  margin: 0;
  padding: 0;
}

.carousel {
  position: relative;
  overflow: hidden;
  width: 100%;
  background: #111;
}

.carousel-inner {
  display: flex;
  transition: none; /* Disable default so GSAP can animate */
  width: 100%;
}

.carousel-item {
  min-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.carousel-image {
  width: 100%;
  height: 480px;
  object-fit: cover;
  display: block;
  will-change: transform;
}

.carousel-control {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.35);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  color: white;
  cursor: pointer;
  font-size: 15px;
  z-index: 10;
  transition: background 0.2s ease, transform 0.2s ease;
}
.carousel-control:hover {
  background: rgba(255, 255, 255, 0.4);
  transform: translateY(-50%) scale(1.06);
}
.prev {
  left: 20px;
}
.next {
  right: 20px;
}
.carousel-dots {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 18px;
  display: flex;
  justify-content: center;
  gap: 8px;
  z-index: 10;
}
.dot {
  height: 8px;
  width: 8px;
  padding: 0;
  border: none;
  background-color: rgba(255, 255, 255, 0.55);
  border-radius: 9999px;
  cursor: pointer;
  transition: width 0.3s ease, background-color 0.3s ease;
}
.dot:hover {
  background-color: rgba(255, 255, 255, 0.85);
}
.dot.active {
  width: 26px;
  background-color: #ffe9a0;
}

/* ---- Responsive Styles ---- */
@media (max-width: 1024px) {
  .carousel-image {
    height: 380px;
  }
}
@media (max-width: 700px) {
  .carousel-image {
    height: 240px;
  }
  .carousel-control {
    width: 34px;
    height: 34px;
    font-size: 12px;
  }
  .prev {
    left: 10px;
  }
  .next {
    right: 10px;
  }
  .carousel-dots {
    bottom: 12px;
  }
}
@media (max-width: 500px) {
  .carousel-image {
    height: 200px;
  }
  .dot {
    height: 6px;
    width: 6px;
  }
  .dot.active {
    width: 18px;
  }
}
</style>
